import { useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, X, Clock, Save, Users, ChevronDown,
  RefreshCw, Check, AlertCircle, Building2, Calendar, RotateCcw,
  Coffee, ChevronRight, Info,
} from 'lucide-react';
import { format, startOfWeek, addDays, differenceInDays } from 'date-fns';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/shared/PageHeader';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { Button } from '@/components/ui/button';
import { useEmployees } from '@/api/queries/employee.queries';
import { useDepartments } from '@/api/queries/settings.queries';
import {
  useShifts, useCreateShift, useUpdateShift, useDeleteShift,
  useShiftAssignments, useAssignShift, useBulkAssignShift, useRemoveShiftAssignment,
  useCyclicPatterns, useCreateCyclicPattern, useUpdateCyclicPattern, useDeleteCyclicPattern,
  useCyclicAssignments, useAssignCyclicPattern, useRemoveCyclicAssignment,
  useCompOffs, useCreateCompOff, useApproveCompOff, useRejectCompOff, useDeleteCompOff,
} from '@/api/queries/attendance.queries';

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'master',     label: 'Shift Master',    icon: Clock        },
  { id: 'assignment', label: 'Shift Assignment', icon: Users        },
  { id: 'cyclic',     label: 'Cyclic Shifts',   icon: RefreshCw    },
  { id: 'compoff',    label: 'Comp Off',         icon: Coffee       },
  { id: 'roster',     label: 'Weekly Roster',    icon: Calendar     },
];

const SHIFT_COLORS_PALETTE = ['#007AFF','#34C759','#FF9500','#FF3B30','#AF52DE','#5AC8FA','#FF2D55','#00C7BE'];

const STATUS_STYLES = {
  PENDING:  'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700',
  APPROVED: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700',
  REJECTED: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700',
};

const COMP_OFF_REASONS = ['Holiday coverage', 'Weekend shift', 'Emergency duty', 'Project deadline', 'Client requirement'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function calcDuration(start, end) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function shiftStyle(hex, selected) {
  return {
    backgroundColor: hex + (selected ? '22' : '14'),
    borderColor: hex + (selected ? 'cc' : '44'),
    color: hex,
  };
}

function ShiftPill({ shift, small }) {
  if (!shift) return <span className="text-[11px] text-[var(--text-tertiary)]">—</span>;
  return (
    <span
      className={cn('inline-flex items-center gap-1 font-semibold rounded-full border', small ? 'text-[10px] px-2 py-0.5' : 'text-[11px] px-2.5 py-1')}
      style={{ backgroundColor: shift.color + '18', borderColor: shift.color + '55', color: shift.color }}>
      {shift.name}
    </span>
  );
}

function EmptyState({ icon: Icon, title, sub }) {
  return (
    <div className="flex flex-col items-center py-16 gap-3 text-center">
      <div className="w-12 h-12 rounded-[14px] bg-[var(--bg-muted)] flex items-center justify-center">
        <Icon size={22} className="text-[var(--text-tertiary)]"/>
      </div>
      <p className="text-[14px] font-semibold text-[var(--text-secondary)]">{title}</p>
      {sub && <p className="text-[12px] text-[var(--text-tertiary)] max-w-xs">{sub}</p>}
    </div>
  );
}

// ─── Shift Modal ──────────────────────────────────────────────────────────────

function ShiftModal({ shift, onClose }) {
  const create = useCreateShift();
  const update = useUpdateShift();
  const [form, setForm] = useState(shift || {
    name: '', start_time: '09:30', end_time: '18:00',
    color: '#007AFF', late_after_minutes: 10, grace_minutes: 5,
  });

  const inputCls = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  const handleSave = async () => {
    if (!form.name.trim() || !form.start_time || !form.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      if (shift) {
        await update.mutateAsync({ id: shift.id, ...form });
        toast.success('Shift updated');
      } else {
        await create.mutateAsync(form);
        toast.success('Shift created');
      }
      onClose();
    } catch {
      toast.error('Failed to save shift');
    }
  };

  const isSaving = create.isPending || update.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[18px] font-bold text-[var(--text-primary)]">{shift ? 'Edit Shift' : 'New Shift'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Shift Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Shift 1 — General" className={inputCls}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[['Start Time', 'start_time'], ['End Time', 'end_time']].map(([label, key]) => (
              <div key={key} className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">{label}</label>
                <input type="time" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className={inputCls}/>
              </div>
            ))}
          </div>
          {form.start_time && form.end_time && (
            <p className="text-[12px] text-[var(--text-tertiary)]">Duration: {calcDuration(form.start_time, form.end_time)}</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Late After (min)</label>
              <input type="number" min="0" value={form.late_after_minutes}
                onChange={e => setForm(f => ({ ...f, late_after_minutes: Number(e.target.value) }))} className={inputCls}/>
            </div>
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Grace Period (min)</label>
              <input type="number" min="0" value={form.grace_minutes}
                onChange={e => setForm(f => ({ ...f, grace_minutes: Number(e.target.value) }))} className={inputCls}/>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Color</label>
            <div className="flex gap-2 flex-wrap">
              {SHIFT_COLORS_PALETTE.map(c => (
                <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color: c }))}
                  style={{ backgroundColor: c }}
                  className={cn('w-7 h-7 rounded-full transition-all', form.color === c ? 'ring-2 ring-offset-2 ring-[var(--accent)]' : '')}/>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={handleSave} loading={isSaving}>
              {shift ? 'Update Shift' : 'Add Shift'}
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TAB 1: Shift Master ──────────────────────────────────────────────────────

function ShiftMasterTab() {
  const { data: shiftsData }   = useShifts();
  const { data: assignData }   = useShiftAssignments();
  const { data: empData }      = useEmployees();
  const { data: cyclicAsgn }   = useCyclicAssignments();
  const deleteShift = useDeleteShift();
  const [modal, setModal] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const shifts      = shiftsData?.data || [];
  const assignments = assignData?.data || [];
  const allEmployees = (empData?.data || []).filter(e => e.status === 'ACTIVE');
  const cyclicEmpIds = new Set((cyclicAsgn?.data || []).map(c => c.employee_id));

  // The first shift is the default — all unassigned employees belong to it
  const defaultShiftId = shifts[0]?.id;

  // Employees explicitly assigned to a non-default shift
  const explicitlyAssignedIds = new Set(assignments.map(a => a.employee_id));

  // Employees implicitly on the default shift = active, not explicit, not cyclic
  const implicitDefaultEmployees = useMemo(() => {
    if (!defaultShiftId) return [];
    return allEmployees.filter(e => {
      const id = e.id || e.employee_id;
      return !explicitlyAssignedIds.has(id) && !cyclicEmpIds.has(id);
    });
  }, [allEmployees, explicitlyAssignedIds, cyclicEmpIds, defaultShiftId]);

  // Employee count per shift (explicit assignments + implicit for default)
  const countByShift = useMemo(() => {
    const map = {};
    assignments.forEach(a => { map[a.shift_id] = (map[a.shift_id] || 0) + 1; });
    if (defaultShiftId) map[defaultShiftId] = (map[defaultShiftId] || 0) + implicitDefaultEmployees.length;
    return map;
  }, [assignments, defaultShiftId, implicitDefaultEmployees]);

  const employeesOnShift = useMemo(() => {
    if (!selectedId) return [];
    if (selectedId === defaultShiftId) {
      // Merge explicit assignments to default shift + all implicit employees
      const explicit = assignments.filter(a => a.shift_id === selectedId);
      const implicitAsAssignment = implicitDefaultEmployees.map(e => ({
        employee_id:   e.id || e.employee_id,
        employee_name: `${e.first_name} ${e.last_name}`,
        department:    typeof e.department === 'string' ? e.department : (e.department?.name || ''),
        shift_id:      defaultShiftId,
      }));
      return [...explicit, ...implicitAsAssignment];
    }
    return assignments.filter(a => a.shift_id === selectedId);
  }, [assignments, selectedId, defaultShiftId, implicitDefaultEmployees]);

  const handleDelete = async (s) => {
    if (countByShift[s.id] > 0) {
      toast.error(`${countByShift[s.id]} employee(s) are on this shift — reassign them first`);
      return;
    }
    if (!confirm(`Delete "${s.name}"?`)) return;
    try {
      await deleteShift.mutateAsync(s.id);
      toast.success('Shift deleted');
      if (selectedId === s.id) setSelectedId(null);
    } catch {
      toast.error('Failed to delete shift');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-[var(--text-secondary)]">{shifts.length} shift{shifts.length !== 1 ? 's' : ''} defined</p>
        <Button size="sm" icon={Plus} onClick={() => setModal({ shift: null })}>New Shift</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: shift cards */}
        <div className="lg:col-span-2 space-y-3">
          {shifts.length === 0 && <EmptyState icon={Clock} title="No shifts defined" sub="Create your first shift to get started."/>}
          {shifts.map(s => {
            const count = countByShift[s.id] || 0;
            const isSelected = selectedId === s.id;
            return (
              <div key={s.id}
                onClick={() => setSelectedId(isSelected ? null : s.id)}
                className={cn(
                  'card p-5 cursor-pointer transition-all border-2',
                  isSelected ? 'border-[var(--accent)] shadow-md' : 'border-transparent hover:border-[var(--border)]'
                )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0"
                      style={{ backgroundColor: s.color + '18' }}>
                      <Clock size={18} style={{ color: s.color }}/>
                    </div>
                    <div>
                      <p className="text-[14px] font-bold text-[var(--text-primary)]">{s.name}</p>
                      <p className="text-[12px] text-[var(--text-tertiary)]">
                        {s.start_time} – {s.end_time} · {calcDuration(s.start_time, s.end_time)}
                      </p>
                      <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
                        Late after {s.late_after_minutes}m · Grace {s.grace_minutes}m
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[var(--bg-muted)] text-[var(--text-secondary)]">
                      {count} emp
                    </span>
                    <button type="button" onClick={e => { e.stopPropagation(); setModal({ shift: s }); }}
                      className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]">
                      <Pencil size={13}/>
                    </button>
                    <button type="button" onClick={e => { e.stopPropagation(); handleDelete(s); }}
                      className="p-1.5 rounded-[6px] hover:bg-red-50 dark:hover:bg-red-900/20 text-[var(--text-tertiary)] hover:text-red-600">
                      <Trash2 size={13}/>
                    </button>
                    <ChevronRight size={14} className={cn('text-[var(--text-tertiary)] transition-transform', isSelected && 'rotate-90')}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: employees on selected shift */}
        <div className="card p-5 h-fit">
          {!selectedId ? (
            <div className="flex flex-col items-center py-8 gap-2 text-center">
              <Info size={20} className="text-[var(--text-tertiary)]"/>
              <p className="text-[13px] text-[var(--text-secondary)]">Click a shift to see assigned employees</p>
            </div>
          ) : (
            <div>
              <p className="text-[13px] font-semibold text-[var(--text-primary)] mb-3">
                {shifts.find(s => s.id === selectedId)?.name} · {employeesOnShift.length} employee{employeesOnShift.length !== 1 ? 's' : ''}
              </p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {employeesOnShift.map(a => (
                  <div key={a.employee_id} className="flex items-center gap-2.5 py-1.5 border-b border-[var(--border)] last:border-0">
                    <Avatar name={a.employee_name} size="xs"/>
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-[var(--text-primary)] truncate">{a.employee_name}</p>
                      <p className="text-[10px] text-[var(--text-tertiary)] truncate">{a.department}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {modal !== null && <ShiftModal shift={modal.shift} onClose={() => setModal(null)}/>}
    </div>
  );
}

// ─── TAB 2: Shift Assignment ──────────────────────────────────────────────────

function ShiftAssignmentTab() {
  const { data: empData }    = useEmployees();
  const { data: deptData }   = useDepartments();
  const { data: shiftsData } = useShifts();
  const { data: assignData } = useShiftAssignments();
  const { data: cyclicData } = useCyclicAssignments();
  const assignShift    = useAssignShift();
  const bulkAssign     = useBulkAssignShift();

  const employees  = (empData?.data  || []).filter(e => e.status === 'ACTIVE');
  const departments = deptData?.data  || [];
  const shifts     = shiftsData?.data || [];
  const assignments = assignData?.data || [];
  const cyclicEmps  = (cyclicData?.data || []).map(c => c.employee_id);

  const [deptFilter, setDeptFilter] = useState('');
  const [shiftDropdown, setShiftDropdown] = useState(null); // employeeId whose dropdown is open
  const [bulkDept, setBulkDept] = useState(null);          // { dept, shiftId }

  const DEFAULT_SHIFT_ID = 'shift-001';

  const assignMap = useMemo(() => {
    const m = {};
    assignments.forEach(a => { m[a.employee_id] = a.shift_id; });
    return m;
  }, [assignments]);

  const getEmployeeShift = (empId) => {
    if (cyclicEmps.includes(empId)) return 'CYCLIC';
    return assignMap[empId] || DEFAULT_SHIFT_ID;
  };

  const getShiftObj = (shiftId) => shifts.find(s => s.id === shiftId) || (shifts[0] || null);

  const filteredEmps = useMemo(() => {
    let list = [...employees];
    if (deptFilter) list = list.filter(e => (typeof e.department === 'string' ? e.department : e.department?.name) === deptFilter);
    return list;
  }, [employees, deptFilter]);

  // Group by department
  const byDept = useMemo(() => {
    const map = new Map();
    filteredEmps.forEach(emp => {
      const dept = typeof emp.department === 'string' ? emp.department : (emp.department?.name || 'General');
      if (!map.has(dept)) map.set(dept, []);
      map.get(dept).push(emp);
    });
    return map;
  }, [filteredEmps]);

  const handleAssign = async (emp, shiftId) => {
    setShiftDropdown(null);
    if (shiftId === 'CYCLIC') { toast.info('Go to Cyclic Shifts tab to assign a cyclic pattern'); return; }
    try {
      await assignShift.mutateAsync({
        employee_id:   emp.id || emp.employee_id,
        employee_code: emp.employee_code,
        employee_name: `${emp.first_name} ${emp.last_name}`,
        department:    typeof emp.department === 'string' ? emp.department : (emp.department?.name || ''),
        designation:   typeof emp.designation === 'string' ? emp.designation : (emp.designation?.name || ''),
        shift_id:      shiftId,
        effective_from: new Date().toISOString().slice(0, 10),
      });
      toast.success('Shift assigned');
    } catch { toast.error('Failed to assign shift'); }
  };

  const handleBulkAssign = async (dept, empList, shiftId) => {
    if (!shiftId) return;
    try {
      await bulkAssign.mutateAsync({
        department: dept, shift_id: shiftId,
        employees: empList.map(e => ({
          employee_id:   e.id || e.employee_id,
          employee_code: e.employee_code,
          employee_name: `${e.first_name} ${e.last_name}`,
          department:    dept,
          designation:   typeof e.designation === 'string' ? e.designation : (e.designation?.name || ''),
        })),
        effective_from: new Date().toISOString().slice(0, 10),
      });
      toast.success(`All ${dept} employees assigned to shift`);
      setBulkDept(null);
    } catch { toast.error('Bulk assign failed'); }
  };

  const allDepts = [...new Set(employees.map(e => typeof e.department === 'string' ? e.department : e.department?.name).filter(Boolean))].sort();

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
          className="h-9 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]">
          <option value="">All Departments</option>
          {allDepts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <p className="text-[13px] text-[var(--text-secondary)]">{filteredEmps.length} employee{filteredEmps.length !== 1 ? 's' : ''}</p>
      </div>

      {shifts.length === 0 && (
        <div className="card p-5 flex items-center gap-3 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700">
          <AlertCircle size={16}/> <p className="text-[13px]">Go to Shift Master tab to create shifts first.</p>
        </div>
      )}

      {/* Department groups */}
      {Array.from(byDept.entries()).map(([dept, emps]) => (
        <div key={dept} className="card p-0 overflow-hidden">
          {/* Dept header + bulk assign */}
          <div className="flex items-center justify-between px-5 py-3 bg-[var(--bg-muted)] border-b border-[var(--border)]">
            <div className="flex items-center gap-2">
              <Building2 size={14} className="text-[var(--text-tertiary)]"/>
              <span className="text-[13px] font-bold text-[var(--text-primary)]">{dept}</span>
              <span className="text-[11px] text-[var(--text-tertiary)]">· {emps.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <select
                defaultValue=""
                onChange={e => setBulkDept({ dept, shiftId: e.target.value })}
                className="h-7 px-2 text-[11px] bg-[var(--bg-card)] border border-[var(--border)] rounded-[6px] text-[var(--text-primary)] focus:outline-none">
                <option value="">Bulk assign to…</option>
                {shifts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {bulkDept?.dept === dept && bulkDept?.shiftId && (
                <Button size="xs" onClick={() => handleBulkAssign(dept, emps, bulkDept.shiftId)} loading={bulkAssign.isPending}>
                  Apply
                </Button>
              )}
            </div>
          </div>

          {/* Employee rows */}
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                {['Employee', 'Designation', 'Type', 'Current Shift', 'Change'].map(h => (
                  <th key={h} className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {emps.map(emp => {
                const empId = emp.id || emp.employee_id;
                const currentShiftId = getEmployeeShift(empId);
                const isCyclic = currentShiftId === 'CYCLIC';
                const currentShift = isCyclic ? null : getShiftObj(currentShiftId);
                const isOpen = shiftDropdown === empId;

                return (
                  <tr key={empId} className="hover:bg-[var(--bg-muted)]/40">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Avatar name={`${emp.first_name} ${emp.last_name}`} size="xs"/>
                        <div>
                          <p className="text-[12px] font-semibold text-[var(--text-primary)]">{emp.first_name} {emp.last_name}</p>
                          <p className="text-[10px] text-[var(--text-tertiary)]">{emp.employee_code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-[var(--text-secondary)]">
                      {typeof emp.designation === 'string' ? emp.designation : emp.designation?.name}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[var(--bg-muted)] text-[var(--text-secondary)]">
                        {emp.employment_type?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {isCyclic
                        ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-2 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-700"><RefreshCw size={9}/> Cyclic</span>
                        : <ShiftPill shift={currentShift} small/>
                      }
                    </td>
                    <td className="px-4 py-2.5 relative">
                      {!isCyclic && (
                        <>
                          <button type="button" onClick={() => setShiftDropdown(isOpen ? null : empId)}
                            className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-[8px] border border-[var(--border)] bg-[var(--bg-muted)] hover:border-[var(--accent)] text-[var(--text-secondary)] transition-all">
                            Change <ChevronDown size={11}/>
                          </button>
                          {isOpen && (
                            <div className="absolute top-full left-0 z-30 mt-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-[12px] shadow-lg p-1.5 min-w-[160px]">
                              {shifts.map(s => (
                                <button key={s.id} type="button"
                                  onClick={() => handleAssign(emp, s.id)}
                                  className={cn(
                                    'w-full flex items-center gap-2.5 text-left text-[12px] px-3 py-2 rounded-[8px] transition-colors hover:bg-[var(--bg-muted)]',
                                    currentShiftId === s.id ? 'font-bold text-[var(--accent)]' : 'text-[var(--text-primary)]'
                                  )}>
                                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }}/>
                                  {s.name}
                                  {currentShiftId === s.id && <Check size={11} className="ml-auto text-[var(--accent)]"/>}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      {isCyclic && <span className="text-[11px] text-[var(--text-tertiary)]">Cyclic tab</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {filteredEmps.length === 0 && <EmptyState icon={Users} title="No employees found" sub="Adjust department filter or add employees first."/>}

      {/* Click-away to close dropdown */}
      {shiftDropdown && <div className="fixed inset-0 z-20" onClick={() => setShiftDropdown(null)}/>}
    </div>
  );
}

// ─── TAB 3: Cyclic Shifts ─────────────────────────────────────────────────────

function CyclicShiftsTab() {
  const { data: shiftsData }   = useShifts();
  const { data: empData }      = useEmployees();
  const { data: patternsData } = useCyclicPatterns();
  const { data: cyclicAsgn }   = useCyclicAssignments();
  const createPattern  = useCreateCyclicPattern();
  const updatePattern  = useUpdateCyclicPattern();
  const deletePattern  = useDeleteCyclicPattern();
  const assignCyclic   = useAssignCyclicPattern();
  const removeCyclic   = useRemoveCyclicAssignment();

  const shifts   = shiftsData?.data   || [];
  const employees = (empData?.data    || []).filter(e => e.status === 'ACTIVE');
  const patterns = patternsData?.data || [];
  const cyclicAssignments = cyclicAsgn?.data || [];

  const [patternModal, setPatternModal] = useState(null); // null | { pattern }
  const [assignModal, setAssignModal]   = useState(false);
  const [newPattern, setNewPattern]     = useState({ name: '', cycles: [{ shift_id: '', duration_days: 7 }] });
  const [newAssign, setNewAssign]       = useState({ employee_id: '', pattern_id: '', start_date: new Date().toISOString().slice(0, 10) });

  // Compute active shift for today for each cyclic assignment
  const today = new Date().toISOString().slice(0, 10);

  const computeActiveShiftFrontend = useCallback((assignment) => {
    const pattern = patterns.find(p => p.id === assignment.pattern_id);
    if (!pattern || !pattern.cycles.length) return null;
    const start = new Date(assignment.start_date);
    const target = new Date(today);
    const dayOffset = Math.floor((target - start) / 86400000);
    if (dayOffset < 0) return null;
    const totalDays = pattern.cycles.reduce((s, c) => s + c.duration_days, 0);
    const offsetInCycle = dayOffset % totalDays;
    let cum = 0;
    for (const cycle of pattern.cycles) {
      cum += cycle.duration_days;
      if (offsetInCycle < cum) return shifts.find(s => s.id === cycle.shift_id) || null;
    }
    return null;
  }, [patterns, shifts, today]);

  // Days until next cycle switch
  const daysUntilSwitch = useCallback((assignment) => {
    const pattern = patterns.find(p => p.id === assignment.pattern_id);
    if (!pattern || !pattern.cycles.length) return null;
    const start = new Date(assignment.start_date);
    const target = new Date(today);
    const dayOffset = Math.floor((target - start) / 86400000);
    if (dayOffset < 0) return null;
    const totalDays = pattern.cycles.reduce((s, c) => s + c.duration_days, 0);
    const offsetInCycle = dayOffset % totalDays;
    let cum = 0;
    for (const cycle of pattern.cycles) {
      cum += cycle.duration_days;
      if (offsetInCycle < cum) return cum - offsetInCycle;
    }
    return null;
  }, [patterns, today]);

  const addCycle = () => setNewPattern(p => ({ ...p, cycles: [...p.cycles, { shift_id: '', duration_days: 7 }] }));
  const removeCycle = (idx) => setNewPattern(p => ({ ...p, cycles: p.cycles.filter((_, i) => i !== idx) }));
  const updateCycle = (idx, key, val) => setNewPattern(p => ({
    ...p, cycles: p.cycles.map((c, i) => i === idx ? { ...c, [key]: val } : c),
  }));

  const handleSavePattern = async () => {
    if (!newPattern.name.trim() || newPattern.cycles.some(c => !c.shift_id)) {
      toast.error('Please fill all fields'); return;
    }
    try {
      if (patternModal?.pattern) {
        await updatePattern.mutateAsync({ id: patternModal.pattern.id, ...newPattern });
        toast.success('Pattern updated');
      } else {
        await createPattern.mutateAsync(newPattern);
        toast.success('Pattern created');
      }
      setPatternModal(null);
      setNewPattern({ name: '', cycles: [{ shift_id: '', duration_days: 7 }] });
    } catch { toast.error('Failed to save pattern'); }
  };

  const handleAssign = async () => {
    if (!newAssign.employee_id || !newAssign.pattern_id || !newAssign.start_date) {
      toast.error('Fill all fields'); return;
    }
    const emp = employees.find(e => (e.id || e.employee_id) === newAssign.employee_id);
    try {
      await assignCyclic.mutateAsync({
        employee_id:   newAssign.employee_id,
        employee_code: emp?.employee_code || '',
        employee_name: emp ? `${emp.first_name} ${emp.last_name}` : '',
        department:    typeof emp?.department === 'string' ? emp.department : (emp?.department?.name || ''),
        designation:   typeof emp?.designation === 'string' ? emp.designation : (emp?.designation?.name || ''),
        pattern_id:    newAssign.pattern_id,
        start_date:    newAssign.start_date,
      });
      toast.success('Cyclic pattern assigned');
      setAssignModal(false);
      setNewAssign({ employee_id: '', pattern_id: '', start_date: today });
    } catch { toast.error('Failed to assign pattern'); }
  };

  const selectCls = "h-9 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";
  const inputCls  = "h-9 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  return (
    <div className="space-y-6">
      {/* Patterns section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[15px] font-bold text-[var(--text-primary)]">Rotation Patterns</p>
            <p className="text-[12px] text-[var(--text-tertiary)]">Define named shift rotation sequences</p>
          </div>
          <Button size="sm" icon={Plus} onClick={() => { setPatternModal({ pattern: null }); setNewPattern({ name: '', cycles: [{ shift_id: '', duration_days: 7 }] }); }}>
            New Pattern
          </Button>
        </div>

        {patterns.length === 0 && <EmptyState icon={RefreshCw} title="No patterns yet" sub="Create a cyclic pattern like 'A/B Weekly Rotation' to get started."/>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {patterns.map(p => (
            <div key={p.id} className="card p-4 space-y-3">
              <div className="flex items-start justify-between">
                <p className="text-[13px] font-bold text-[var(--text-primary)]">{p.name}</p>
                <div className="flex gap-1">
                  <button type="button" onClick={() => { setPatternModal({ pattern: p }); setNewPattern({ name: p.name, cycles: p.cycles }); }}
                    className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><Pencil size={12}/></button>
                  <button type="button" onClick={async () => { try { await deletePattern.mutateAsync(p.id); toast.success('Pattern deleted'); } catch (e) { toast.error(e?.response?.data?.error?.message || 'Delete failed'); } }}
                    className="p-1.5 rounded-[6px] hover:bg-red-50 dark:hover:bg-red-900/20 text-[var(--text-tertiary)] hover:text-red-600"><Trash2 size={12}/></button>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {p.cycles.map((c, i) => {
                  const s = shifts.find(sh => sh.id === c.shift_id);
                  return (
                    <div key={i} className="flex items-center gap-1">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border"
                        style={{ backgroundColor: (s?.color || '#888') + '18', borderColor: (s?.color || '#888') + '44', color: s?.color || '#888' }}>
                        {s?.name || c.shift_id}
                      </span>
                      <span className="text-[10px] text-[var(--text-tertiary)]">×{c.duration_days}d</span>
                      {i < p.cycles.length - 1 && <ChevronRight size={9} className="text-[var(--text-tertiary)]"/>}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-[var(--text-tertiary)]">
                Cycle: {p.cycles.reduce((s, c) => s + c.duration_days, 0)} days total
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Employee assignments */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[15px] font-bold text-[var(--text-primary)]">Employee Assignments</p>
            <p className="text-[12px] text-[var(--text-tertiary)]">Employees on cyclic rotation</p>
          </div>
          {patterns.length > 0 && (
            <Button size="sm" icon={Plus} onClick={() => setAssignModal(true)}>Assign Employee</Button>
          )}
        </div>

        {cyclicAssignments.length === 0
          ? <EmptyState icon={Users} title="No cyclic assignments" sub="Create a pattern above, then assign employees to it."/>
          : (
          <div className="card p-0 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--bg-muted)]">
                  {['Employee', 'Department', 'Pattern', 'Start Date', 'Active Shift Today', 'Switch In', ''].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {cyclicAssignments.map(a => {
                  const activeShift = computeActiveShiftFrontend(a);
                  const switchIn = daysUntilSwitch(a);
                  const pattern = patterns.find(p => p.id === a.pattern_id);
                  return (
                    <tr key={a.employee_id} className="hover:bg-[var(--bg-muted)]/40">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <Avatar name={a.employee_name} size="xs"/>
                          <div>
                            <p className="text-[12px] font-semibold text-[var(--text-primary)]">{a.employee_name}</p>
                            <p className="text-[10px] text-[var(--text-tertiary)]">{a.employee_code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-[var(--text-secondary)]">{a.department}</td>
                      <td className="px-4 py-2.5 text-[12px] font-semibold text-[var(--text-primary)]">{pattern?.name || a.pattern_id}</td>
                      <td className="px-4 py-2.5 text-[12px] text-[var(--text-secondary)]">{a.start_date}</td>
                      <td className="px-4 py-2.5">
                        {activeShift
                          ? <ShiftPill shift={activeShift} small/>
                          : <span className="text-[11px] text-[var(--text-tertiary)]">Not started</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        {switchIn != null && (
                          <span className={cn('text-[11px] font-semibold', switchIn <= 3 ? 'text-amber-600' : 'text-[var(--text-tertiary)]')}>
                            {switchIn === 1 ? 'Tomorrow' : `${switchIn}d`}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <button type="button"
                          onClick={async () => { try { await removeCyclic.mutateAsync(a.employee_id); toast.success('Assignment removed'); } catch { toast.error('Remove failed'); } }}
                          className="text-[11px] text-red-500 hover:text-red-700 font-semibold">
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pattern modal */}
      {patternModal !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && setPatternModal(null)}>
          <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-md border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
              <h2 className="text-[18px] font-bold text-[var(--text-primary)]">{patternModal.pattern ? 'Edit Pattern' : 'New Rotation Pattern'}</h2>
              <button onClick={() => setPatternModal(null)} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Pattern Name</label>
                <input value={newPattern.name} onChange={e => setNewPattern(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. A/B Weekly Rotation" className={inputCls + ' w-full'}/>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[13px] font-semibold text-[var(--text-primary)]">Rotation Cycles</label>
                  <button type="button" onClick={addCycle} className="text-[12px] font-semibold text-[var(--accent)] hover:underline flex items-center gap-1">
                    <Plus size={12}/> Add Cycle
                  </button>
                </div>
                {newPattern.cycles.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 rounded-[10px] bg-[var(--bg-muted)] border border-[var(--border)]">
                    <span className="text-[11px] font-bold text-[var(--text-tertiary)] w-5 shrink-0">{i + 1}</span>
                    <select value={c.shift_id} onChange={e => updateCycle(i, 'shift_id', e.target.value)}
                      className={selectCls + ' flex-1'}>
                      <option value="">Pick shift…</option>
                      {shifts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <input type="number" min="1" max="90" value={c.duration_days}
                      onChange={e => updateCycle(i, 'duration_days', Number(e.target.value))}
                      className={inputCls + ' w-16'}/>
                    <span className="text-[11px] text-[var(--text-tertiary)] shrink-0">days</span>
                    {newPattern.cycles.length > 1 && (
                      <button type="button" onClick={() => removeCycle(i)} className="text-red-500 hover:text-red-700 shrink-0"><X size={13}/></button>
                    )}
                  </div>
                ))}
                <p className="text-[11px] text-[var(--text-tertiary)]">
                  Total cycle: {newPattern.cycles.reduce((s, c) => s + (Number(c.duration_days) || 0), 0)} days
                </p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button className="flex-1" onClick={handleSavePattern} loading={createPattern.isPending || updatePattern.isPending}>
                  {patternModal.pattern ? 'Update' : 'Create Pattern'}
                </Button>
                <Button variant="outline" onClick={() => setPatternModal(null)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && setAssignModal(false)}>
          <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
              <h2 className="text-[17px] font-bold text-[var(--text-primary)]">Assign Cyclic Pattern</h2>
              <button onClick={() => setAssignModal(false)} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Employee</label>
                <select value={newAssign.employee_id} onChange={e => setNewAssign(a => ({ ...a, employee_id: e.target.value }))}
                  className={selectCls + ' w-full'}>
                  <option value="">Select employee…</option>
                  {employees.map(e => <option key={e.id || e.employee_id} value={e.id || e.employee_id}>{e.first_name} {e.last_name} ({e.employee_code})</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Rotation Pattern</label>
                <select value={newAssign.pattern_id} onChange={e => setNewAssign(a => ({ ...a, pattern_id: e.target.value }))}
                  className={selectCls + ' w-full'}>
                  <option value="">Select pattern…</option>
                  {patterns.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Rotation Start Date</label>
                <input type="date" value={newAssign.start_date} onChange={e => setNewAssign(a => ({ ...a, start_date: e.target.value }))}
                  className={inputCls + ' w-full'}/>
              </div>
              <div className="flex gap-2 pt-1">
                <Button className="flex-1" onClick={handleAssign} loading={assignCyclic.isPending}>Assign</Button>
                <Button variant="outline" onClick={() => setAssignModal(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TAB 4: Comp Off ─────────────────────────────────────────────────────────

function CompOffTab() {
  const { data: empData }     = useEmployees();
  const { data: compOffData } = useCompOffs();
  const createCompOff  = useCreateCompOff();
  const approveCompOff = useApproveCompOff();
  const rejectCompOff  = useRejectCompOff();
  const deleteCompOff  = useDeleteCompOff();

  const employees = (empData?.data     || []).filter(e => e.status === 'ACTIVE');
  const compOffs  = compOffData?.data  || [];

  const [modal, setModal]     = useState(false);
  const [rejectModal, setRejectModal] = useState(null); // { id }
  const [rejectReason, setRejectReason] = useState('');
  const [form, setForm] = useState({
    employee_id: '', worked_date: '', worked_reason: '', comp_off_date: '',
  });

  const stats = useMemo(() => ({
    pending:  compOffs.filter(c => c.status === 'PENDING').length,
    approved: compOffs.filter(c => c.status === 'APPROVED').length,
    thisMonth: compOffs.filter(c => c.status === 'APPROVED' && c.applied_on?.startsWith(new Date().toISOString().slice(0, 7))).length,
  }), [compOffs]);

  const handleCreate = async () => {
    if (!form.employee_id || !form.worked_date || !form.worked_reason) {
      toast.error('Please fill in all required fields'); return;
    }
    const emp = employees.find(e => (e.id || e.employee_id) === form.employee_id);
    try {
      await createCompOff.mutateAsync({
        employee_id:   form.employee_id,
        employee_name: emp ? `${emp.first_name} ${emp.last_name}` : '',
        employee_code: emp?.employee_code || '',
        department:    typeof emp?.department === 'string' ? emp.department : (emp?.department?.name || ''),
        worked_date:   form.worked_date,
        worked_reason: form.worked_reason,
        comp_off_date: form.comp_off_date || null,
      });
      toast.success('Comp off created');
      setModal(false);
      setForm({ employee_id: '', worked_date: '', worked_reason: '', comp_off_date: '' });
    } catch { toast.error('Failed to create comp off'); }
  };

  const handleApprove = async (id) => {
    try { await approveCompOff.mutateAsync(id); toast.success('Comp off approved'); }
    catch { toast.error('Failed to approve'); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Please provide a rejection reason'); return; }
    try {
      await rejectCompOff.mutateAsync({ id: rejectModal.id, reason: rejectReason });
      toast.success('Comp off rejected');
      setRejectModal(null); setRejectReason('');
    } catch { toast.error('Failed to reject'); }
  };

  const inputCls  = "w-full h-9 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";
  const selectCls = inputCls;

  return (
    <div className="space-y-5">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Pending Approval', value: stats.pending, color: 'amber' },
          { label: 'Approved', value: stats.approved, color: 'green' },
          { label: 'Approved This Month', value: stats.thisMonth, color: 'blue' },
        ].map(s => (
          <div key={s.label} className="card p-4 text-center">
            <p className={`text-[28px] font-black text-${s.color}-600 dark:text-${s.color}-400`}>{s.value}</p>
            <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Table header */}
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-[var(--text-secondary)]">{compOffs.length} comp-off record{compOffs.length !== 1 ? 's' : ''}</p>
        <Button size="sm" icon={Plus} onClick={() => setModal(true)}>Add Comp Off</Button>
      </div>

      {compOffs.length === 0
        ? <EmptyState icon={Coffee} title="No comp-off records" sub="Add a comp off when an employee works on a holiday or Sunday."/>
        : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-muted)]">
                {['Employee', 'Dept', 'Worked On', 'Reason', 'CO Date', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {compOffs.map(c => (
                <tr key={c.id} className="hover:bg-[var(--bg-muted)]/40">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Avatar name={c.employee_name} size="xs"/>
                      <div>
                        <p className="text-[12px] font-semibold text-[var(--text-primary)]">{c.employee_name}</p>
                        <p className="text-[10px] text-[var(--text-tertiary)]">{c.employee_code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-[var(--text-secondary)]">{c.department}</td>
                  <td className="px-4 py-2.5 text-[12px] text-[var(--text-primary)]">{c.worked_date}</td>
                  <td className="px-4 py-2.5 text-[12px] text-[var(--text-secondary)] max-w-[150px] truncate">{c.worked_reason}</td>
                  <td className="px-4 py-2.5 text-[12px] text-[var(--text-secondary)]">{c.comp_off_date || <span className="text-[var(--text-tertiary)]">Not claimed</span>}</td>
                  <td className="px-4 py-2.5">
                    <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full border', STATUS_STYLES[c.status])}>{c.status}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      {c.status === 'PENDING' && (
                        <>
                          <button type="button" onClick={() => handleApprove(c.id)}
                            className="text-[11px] font-semibold text-green-600 hover:text-green-800 dark:text-green-400 px-2 py-1 rounded-[6px] hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                            Approve
                          </button>
                          <button type="button" onClick={() => setRejectModal({ id: c.id })}
                            className="text-[11px] font-semibold text-red-500 hover:text-red-700 px-2 py-1 rounded-[6px] hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                            Reject
                          </button>
                        </>
                      )}
                      <button type="button" onClick={async () => { try { await deleteCompOff.mutateAsync(c.id); toast.success('Deleted'); } catch { toast.error('Delete failed'); } }}
                        className="p-1 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)] hover:text-red-500">
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
              <h2 className="text-[17px] font-bold text-[var(--text-primary)]">Add Comp Off</h2>
              <button onClick={() => setModal(false)} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Employee *</label>
                <select value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}
                  className={selectCls}>
                  <option value="">Select employee…</option>
                  {employees.map(e => <option key={e.id || e.employee_id} value={e.id || e.employee_id}>{e.first_name} {e.last_name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Date Worked *</label>
                <input type="date" value={form.worked_date} onChange={e => setForm(f => ({ ...f, worked_date: e.target.value }))} className={inputCls}/>
              </div>
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Reason *</label>
                <select value={form.worked_reason} onChange={e => setForm(f => ({ ...f, worked_reason: e.target.value }))} className={selectCls}>
                  <option value="">Select reason…</option>
                  {COMP_OFF_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Comp Off Date <span className="text-[var(--text-tertiary)] font-normal">(optional)</span></label>
                <input type="date" value={form.comp_off_date} onChange={e => setForm(f => ({ ...f, comp_off_date: e.target.value }))} className={inputCls}/>
                <p className="text-[11px] text-[var(--text-tertiary)]">Leave blank if the employee will claim it later.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button className="flex-1" onClick={handleCreate} loading={createCompOff.isPending}>Create</Button>
                <Button variant="outline" onClick={() => setModal(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && setRejectModal(null)}>
          <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
              <h2 className="text-[17px] font-bold text-[var(--text-primary)]">Reject Comp Off</h2>
              <button onClick={() => setRejectModal(null)} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Reason for rejection *</label>
                <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3}
                  className="w-full px-3 py-2.5 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] resize-none"/>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleReject} loading={rejectCompOff.isPending}>Reject</Button>
                <Button variant="outline" onClick={() => setRejectModal(null)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TAB 5: Weekly Roster ─────────────────────────────────────────────────────

function WeeklyRosterTab() {
  const now = new Date();
  const [weekStart, setWeekStart] = useState(startOfWeek(now, { weekStartsOn: 1 }));
  const { data: empResp }         = useEmployees();
  const { data: shiftsData }      = useShifts();
  const { data: assignData }      = useShiftAssignments();
  const { data: cyclicData }      = useCyclicAssignments();
  const assignShift = useAssignShift();

  const employees  = (empResp?.data || []).filter(e => e.status === 'ACTIVE').slice(0, 20);
  const shifts     = shiftsData?.data || [];
  const assignments = assignData?.data || [];
  const cyclicAssignments = cyclicData?.data || [];

  const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const weekDays = DAYS.map((_, i) => addDays(weekStart, i));

  const assignMap = useMemo(() => {
    const m = {};
    assignments.forEach(a => { m[a.employee_id] = a.shift_id; });
    return m;
  }, [assignments]);

  const cyclicMap = useMemo(() => {
    const m = {};
    cyclicAssignments.forEach(a => { m[a.employee_id] = a; });
    return m;
  }, [cyclicAssignments]);

  const DEFAULT_SHIFT_ID = 'shift-001';

  const [editCell, setEditCell] = useState(null);

  // For the weekly roster, shift per cell = employee's base shift (same all week unless cyclic)
  const getCellShift = (empId) => {
    if (cyclicMap[empId]) return 'CYCLIC';
    return assignMap[empId] || DEFAULT_SHIFT_ID;
  };

  const SHIFT_COLORS_CSS = [
    'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
    'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
    'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200',
    'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
    'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
  ];

  const shiftColorCss = (idx) => SHIFT_COLORS_CSS[idx % SHIFT_COLORS_CSS.length];

  const handleCellChange = async (emp, shiftId) => {
    setEditCell(null);
    if (shiftId === 'CYCLIC') return;
    const empId = emp.id || emp.employee_id;
    try {
      await assignShift.mutateAsync({
        employee_id:   empId,
        employee_code: emp.employee_code,
        employee_name: `${emp.first_name} ${emp.last_name}`,
        department:    typeof emp.department === 'string' ? emp.department : (emp.department?.name || ''),
        designation:   typeof emp.designation === 'string' ? emp.designation : (emp.designation?.name || ''),
        shift_id:      shiftId,
        effective_from: new Date().toISOString().slice(0, 10),
      });
    } catch { toast.error('Failed to update shift'); }
  };

  const weekLabel = `${format(weekStart, 'MMM d')} – ${format(addDays(weekStart, 6), 'MMM d, yyyy')}`;
  const SHIFTS_WITH_OFF = [...shifts, { id: 'off', name: 'Off', start_time: '', end_time: '', color: '#888' }];

  return (
    <div className="space-y-4">
      {/* Week nav */}
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-[var(--text-secondary)]">Week of {weekLabel}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setWeekStart(d => addDays(d, -7))}>← Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>This Week</Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(d => addDays(d, 7))}>Next →</Button>
        </div>
      </div>

      {/* Shift legend */}
      <div className="flex gap-2 flex-wrap">
        {shifts.map((s, i) => (
          <div key={s.id} className={cn('text-[11px] font-semibold px-3 py-1 rounded-full', shiftColorCss(i))}>
            {s.name}{s.start_time ? ` · ${s.start_time}–${s.end_time}` : ''}
          </div>
        ))}
        <div className="text-[11px] font-semibold px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">Off</div>
        <div className="text-[11px] font-semibold px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">🔄 Cyclic</div>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--border)]">
              <th className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-4 py-3 sticky left-0 bg-[var(--bg-card)] z-10 min-w-[180px]">Employee</th>
              {weekDays.map((d, i) => (
                <th key={i} className={cn('text-center text-[11px] font-semibold uppercase tracking-wide px-3 py-3 min-w-[90px]',
                  i >= 5 ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-secondary)]')}>
                  <div>{DAYS[i]}</div>
                  <div className="text-[10px] font-normal">{format(d, 'MMM d')}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {employees.map(emp => {
              const empId = emp.id || emp.employee_id;
              const shiftId = getCellShift(empId);
              const isCyclic = shiftId === 'CYCLIC';
              const shift = isCyclic ? null : shifts.find(s => s.id === shiftId);
              const shiftIdx = isCyclic ? -1 : shifts.findIndex(s => s.id === shiftId);

              return (
                <tr key={empId} className="hover:bg-[var(--bg-muted)]/50">
                  <td className="px-4 py-2.5 sticky left-0 bg-[var(--bg-card)] z-10 border-r border-[var(--border)]">
                    <div className="flex items-center gap-2">
                      <Avatar name={`${emp.first_name} ${emp.last_name}`} size="xs"/>
                      <div>
                        <p className="text-[12px] font-medium text-[var(--text-primary)] truncate">{emp.first_name} {emp.last_name}</p>
                        <p className="text-[10px] text-[var(--text-tertiary)]">{emp.employee_code}</p>
                      </div>
                    </div>
                  </td>
                  {DAYS.map((_, dayIdx) => {
                    const isWeekend = dayIdx >= 5;
                    const cellKey = `${empId}-${dayIdx}`;
                    const isOpen = editCell === cellKey;

                    return (
                      <td key={dayIdx} className={cn('px-2 py-2 text-center relative', isWeekend && 'bg-[var(--bg-muted)]/30')}>
                        {isCyclic ? (
                          <div className="text-[10px] font-semibold px-2 py-1 rounded-[6px] bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center gap-1">
                            <RefreshCw size={8}/> Cyclic
                          </div>
                        ) : isWeekend && shiftId === DEFAULT_SHIFT_ID ? (
                          <div className="text-[10px] font-semibold px-2 py-1 rounded-[6px] bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-500">Off</div>
                        ) : (
                          <>
                            <button type="button" onClick={() => setEditCell(isOpen ? null : cellKey)}
                              className={cn('text-[10px] font-semibold px-2 py-1 rounded-[6px] w-full transition-colors', shiftColorCss(shiftIdx))}>
                              {shift?.name?.split('—')[0]?.trim() || 'Shift 1'}
                            </button>
                            {isOpen && (
                              <div className="absolute top-full left-0 z-30 mt-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] shadow-lg p-1 min-w-[130px]">
                                {SHIFTS_WITH_OFF.map((s, si) => (
                                  <button key={s.id} type="button" onClick={() => handleCellChange(emp, s.id)}
                                    className={cn('w-full text-left text-[11px] font-semibold px-3 py-1.5 rounded-[6px] transition-colors hover:bg-[var(--bg-muted)] flex items-center gap-2',
                                      shiftId === s.id ? 'text-[var(--accent)]' : 'text-[var(--text-secondary)]')}>
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color || '#888' }}/>
                                    {s.name}
                                    {s.start_time && <span className="text-[9px] text-[var(--text-tertiary)] ml-auto">{s.start_time}</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {employees.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">No employees to schedule</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editCell && <div className="fixed inset-0 z-20" onClick={() => setEditCell(null)}/>}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function RosterPage() {
  const [activeTab, setActiveTab] = useState('master');

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Roster & Shifts"
        subtitle="Manage shifts, assign employees, configure cyclic rotations and comp offs"
      />

      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-[var(--bg-muted)] rounded-[14px] w-fit">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-[10px] text-[13px] font-semibold transition-all',
                active
                  ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              )}>
              <Icon size={14}/>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'master'     && <ShiftMasterTab/>}
      {activeTab === 'assignment' && <ShiftAssignmentTab/>}
      {activeTab === 'cyclic'     && <CyclicShiftsTab/>}
      {activeTab === 'compoff'    && <CompOffTab/>}
      {activeTab === 'roster'     && <WeeklyRosterTab/>}
    </div>
  );
}
